<template>
  <div class="game-constants-management">
    <el-card shadow="never">
      <template #header>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <h2 style="margin: 0; font-size: 20px; font-weight: 600;">常量设置</h2>
            <p style="margin: 8px 0 0 0; color: #909399; font-size: 14px;">管理游戏常量配置（仅管理员可操作）</p>
          </div>
          <el-button type="primary" @click="showAddDialog = true">
            <el-icon><Plus /></el-icon>
            新增常量
          </el-button>
        </div>
      </template>

      <el-table 
        :data="constants" 
        v-loading="loading"
        stripe
        style="width: 100%"
        empty-text="暂无常量数据，点击新增常量按钮添加常量配置"
      >
        <el-table-column prop="gameKey" label="键" min-width="200">
          <template #default="{ row }">
            <span style="font-weight: 500;">{{ row.gameKey }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="value" label="值" min-width="300" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.value || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" width="180">
          <template #default="{ row }">
            <span style="color: #909399; font-size: 13px;">{{ formatDate(row.updateTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button 
              link 
              size="small" 
              @click="editConstant(row)"
            >
              编辑
            </el-button>
            <el-button 
              type="danger" 
              link 
              size="small" 
              @click="deleteConstant(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="showEditDialog ? '编辑常量' : '新增常量'"
      width="500px"
    >
      <el-form :model="formData" label-width="80px">
        <el-form-item label="键" :required="true">
          <el-input 
            v-model="formData.gameKey" 
            placeholder="请输入常量键"
            :disabled="showEditDialog"
            class="key-input"
            :border="false"
          />
        </el-form-item>
        <el-form-item label="值">
          <el-input 
            v-model="formData.value" 
            type="textarea"
            :rows="10"
            placeholder="请输入常量值"
            class="value-textarea"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="saveConstant" :loading="loading">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Plus } from '@element-plus/icons-vue'
import gameConstantsAPI from '@/api/gameConstants'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'GameConstantsManagement',
  components: {
    Plus
  },
  data() {
    return {
      constants: [],
      showAddDialog: false,
      showEditDialog: false,
      formData: {
        game: '尤弥尔传奇国际服',
        gameKey: '',
        value: ''
      },
      loading: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.showAddDialog || this.showEditDialog
      },
      set(val) {
        if (!val) {
          this.closeDialog()
        }
      }
    }
  },
  mounted() {
    this.loadConstants()
  },
  methods: {
    async loadConstants() {
      try {
        this.loading = true
        const response = await gameConstantsAPI.getConstants('尤弥尔传奇国际服')
        // 响应拦截器已经返回了 response.data，所以 response 就是后端返回的对象
        if (response && response.code === 200) {
          this.constants = response.data || []
        } else {
          ElMessage.error(response?.message || '获取常量列表失败')
        }
      } catch (error) {
        console.error('获取常量列表失败:', error)
        ElMessage.error('获取常量列表失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    editConstant(constant) {
      this.formData = {
        game: constant.game || '尤弥尔传奇国际服',
        gameKey: constant.gameKey,
        value: constant.value || ''
      }
      this.showEditDialog = true
    },
    
    async saveConstant() {
      if (!this.formData.gameKey || !this.formData.gameKey.trim()) {
        ElMessage.warning('请输入常量键')
        return
      }
      
      try {
        this.loading = true
        const response = await gameConstantsAPI.saveConstant({
          game: this.formData.game,
          gameKey: this.formData.gameKey.trim(),
          value: this.formData.value || ''
        })
        
        if (response && response.code === 200) {
          ElMessage.success('保存成功')
          this.closeDialog()
          this.loadConstants()
        } else {
          ElMessage.error(response?.message || '保存失败')
        }
      } catch (error) {
        console.error('保存常量失败:', error)
        ElMessage.error('保存常量失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    async deleteConstant(constant) {
      try {
        await ElMessageBox.confirm(
          `确定要删除常量 "${constant.gameKey}" 吗？`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        this.loading = true
        const response = await gameConstantsAPI.deleteConstant(
          constant.game || '尤弥尔传奇国际服',
          constant.gameKey
        )
        
        if (response && response.code === 200) {
          ElMessage.success('删除成功')
          this.loadConstants()
        } else {
          ElMessage.error(response?.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除常量失败:', error)
          ElMessage.error('删除常量失败: ' + (error.response?.data?.message || error.message))
        }
      } finally {
        this.loading = false
      }
    },
    
    closeDialog() {
      this.showAddDialog = false
      this.showEditDialog = false
      this.formData = {
        game: '尤弥尔传奇国际服',
        gameKey: '',
        value: ''
      }
    },
    
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.game-constants-management {
  padding: 20px 20px 32px;
  box-sizing: border-box;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 键输入框样式 - 降低高度并移除边框 */
:deep(.key-input) {
  height: 32px;
}

:deep(.key-input .el-input__wrapper) {
  padding: 4px 11px;
  min-height: 32px;
  border: none !important;
  box-shadow: none !important;
  background-color: transparent;
}

:deep(.key-input .el-input__wrapper:hover) {
  box-shadow: none !important;
}

:deep(.key-input.is-focus .el-input__wrapper) {
  box-shadow: none !important;
  border: none !important;
}

/* 值输入框样式 - 增大尺寸 */
:deep(.value-textarea) {
  min-height: 200px;
}

:deep(.value-textarea .el-textarea__inner) {
  min-height: 200px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  line-height: 1.6;
}
</style>
